import {
    Avatar,
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import AddIcon from '@mui/icons-material/Add';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { Timeline as TimelineIcon, TrackChanges as TargetIcon } from "@mui/icons-material";
import AddExercisesDialog from "../new_templates/AddExercisesDialog";
import { targetMuscles } from "../../enums/TargetMuscles";
import { TempoTypes } from "../../enums/TempoTypes";
import { ManipulationTypes } from "../../enums/ManipulationTypes";
import { typeSets } from "../../enums/TypeSets";
import { RTL } from "../../cache/CacheRtl";
import { getByTypes, getExercise } from "../../services/ExcersiceService";
import { getProgramExercise, updateProgramExerciseRangesById } from "../../services/ExerciseHistoryService";
import { getErrorMessage } from "../../enums/ErrorMessages";
import { useMessageContext } from "../../contexts/MessageContext";
import ExerciseHistoryDialog from "./ExerciseHistoryDialog";
import TraineeSwapNotice from "./TraineeSwapNotice";
import { SUPERSET_COLORS } from "../../utils/exerciseUtils";
import { imageUrl } from "../../utils/imageUrl";
import { nonNegative, nonNegativeInputProps } from "../../utils/numberInput";

const getManipulationLabel = (value) => {
    const type = ManipulationTypes.find(t => t.value === value);
    return type ? type.label : value;
};

const getTargetMuscleLabel = (value) => {
    return targetMuscles.find(x => x.value === value)?.name || value || '';
};

const ProgramExerciseCard = ({
    exercise,
    editMode,
    setEditWorkout,
    prvWorkout,
    prvWorkoutIdx,
    numberOfElements,
    blockIdx,
    weekIdx,
    openIntervals,
    setOpenIntervals,
    traineeId,
    allExercises,
    onLinkAsSuperSet,
}) => {
    const exerciseKey = exercise.uuid || exercise.id || exercise.index;
    const isExpanded = openIntervals?.[exerciseKey] || false;
    const setExpanded = (value) => {
        setOpenIntervals(prev => ({
            ...prev,
            [exerciseKey]: typeof value === 'function' ? value(prev?.[exerciseKey] || false) : value
        }));
    };

    const [loading, setLoading] = useState(true);
    const [exerciseHistoryOpen, setExerciseHistoryOpen] = useState(false);
    const [changeExerciseDialogOpen, setChangeExerciseDialogOpen] = useState(false);
    const [editConfigDialogOpen, setEditConfigDialogOpen] = useState(false);
    const [linkSuperSetDialogOpen, setLinkSuperSetDialogOpen] = useState(false);
    const { notificationHandler } = useMessageContext();

    const [selectedValues, setSelectedValues] = useState({
        targetMuscle: '',
        exerciseDef: null,
        exerciseOptions: []
    });
    const [hasExerciseTarget, setHasExerciseTarget] = useState(false);
    const [exerciseRange, setExerciseRange] = useState({ intervals: [] });

    const isPartOfSuperSet = !!exercise.superSetGroupId;

    const prvExercise = prvWorkout?.programExercises?.find(x => x.uuid === exercise.uuid);

    const refreshExerciseTarget = () => {
        if (traineeId && exercise.exerciseId) {
            getProgramExercise(traineeId, exercise.exerciseId)
                .then(res => {
                    const pe = res.data;
                    setHasExerciseTarget(pe?.targetWeight > 0 && pe?.targetReps > 0);
                })
                .catch(() => setHasExerciseTarget(false));
        }
    };

    useEffect(() => {
        setLoading(true);
        if (exercise.exerciseId) {
            getExercise(exercise.exerciseId).then(res => {
                getByTypes(res.data.targetMuscle)
                    .then(resEx => {
                        setSelectedValues({ targetMuscle: res.data.targetMuscle, exerciseDef: res.data, exerciseOptions: resEx.data });
                        setLoading(false);
                    }).catch(e => { setLoading(false); notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)); });
            }).catch(e => { setLoading(false); notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)); });

            setExerciseRange({
                intervals: exercise.intervals?.map(i => ({ index: i.index, minReps: i.minReps ?? null, maxReps: i.maxReps ?? null })) || []
            });
            if (traineeId) refreshExerciseTarget();
        } else {
            setSelectedValues({ targetMuscle: '', exerciseDef: null, exerciseOptions: [] });
            setHasExerciseTarget(false);
            setExerciseRange({
                intervals: exercise.intervals?.map(i => ({ index: i.index, minReps: i.minReps ?? null, maxReps: i.maxReps ?? null })) || []
            });
            setLoading(false);
        }
    }, [exercise, traineeId]);

    // --- All handlers preserved from WeekRow ---
    const handleIntervalRangeChange = (intervalIndex, field, value) => {
        setExerciseRange(prev => {
            const newIntervals = [...(prev.intervals || [])];
            const idx = newIntervals.findIndex(i => i.index === intervalIndex);
            if (idx >= 0) { newIntervals[idx] = { ...newIntervals[idx], [field]: value }; }
            else { newIntervals.push({ index: intervalIndex, [field]: value }); }
            if (exercise.id) {
                updateProgramExerciseRangesById(exercise.id, { intervals: newIntervals }).catch(() => notificationHandler.error('שגיאה בשמירת טווח'));
            }
            return { intervals: newIntervals };
        });
        setEditWorkout(prv => {
            let nw = { ...prv };
            let pe = nw.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
            if (pe.at(exercise.index).intervals) {
                const ii = pe.at(exercise.index).intervals.findIndex(i => i.index === intervalIndex);
                if (ii >= 0) { pe.at(exercise.index).intervals[ii] = { ...pe.at(exercise.index).intervals[ii], [field]: value }; }
            }
            return nw;
        });
    };

    const changeExercise = (exerciseObj) => {
        setSelectedValues(prv => ({ ...prv, exerciseDef: exerciseObj }));
        setEditWorkout(prv => {
            let nw = { ...prv }; let pe = nw.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
            pe.at(exercise.index).exerciseId = exerciseObj.id; pe.at(exercise.index).comment = "";
            if (pe.at(exercise.index).intervals) {
                const newMin = exerciseObj?.defaultMinReps ?? null;
                const newMax = exerciseObj?.defaultMaxReps ?? null;
                const midReps = (newMin != null && newMax != null) ? Math.round((newMin + newMax) / 2) : null;
                const ui = pe.at(exercise.index).intervals.map(i => ({ ...i, minReps: newMin, maxReps: newMax, recommendedReps: midReps, recommendedWeight: null, weight: null }));
                pe.at(exercise.index).intervals = ui;
                setExerciseRange({ intervals: ui.map(i => ({ index: i.index, minReps: i.minReps, maxReps: i.maxReps })) });
            }
            nw.programExercises = pe; return nw;
        });
    };

    const handleChangeExerciseFromDialog = (selectedExercises) => {
        if (selectedExercises.length > 0) {
            changeExercise(selectedExercises[0]);
            // Also update the target muscle on the workout exercise
            setEditWorkout(prv => {
                let nw = { ...prv }; let pe = nw.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
                pe.at(exercise.index).targetMuscle = selectedExercises[0].targetMuscle;
                nw.programExercises = pe; return nw;
            });
        }
    };

    const addInterval = () => {
        setEditWorkout(prv => {
            let nw = { ...prv }; let pe = nw.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
            let intervals = pe.at(exercise.index).intervals || [];
            const isFirst = blockIdx === 0 && weekIdx === 0;
            const sorted = [...intervals].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
            const last = sorted.length > 0 ? sorted[sorted.length - 1] : null;
            const minReps = last?.minReps ?? selectedValues.exerciseDef?.defaultMinReps ?? null;
            const maxReps = last?.maxReps ?? selectedValues.exerciseDef?.defaultMaxReps ?? null;
            const recommendedReps = (minReps != null && maxReps != null) ? Math.round((minReps + maxReps) / 2) : 10;
            intervals.push({ index: intervals.length, recommendedReps, typeSet: isFirst ? "RM_SET" : "REGULAR_SET", minReps, maxReps });
            pe.at(exercise.index).intervals = intervals;
            setExerciseRange({ intervals: intervals.map(i => ({ index: i.index, minReps: i.minReps, maxReps: i.maxReps })) });
            nw.programExercises = pe; return nw;
        });
    };

    const deleteInterval = (intervalIndex) => {
        setEditWorkout(prv => {
            let nw = { ...prv }; nw.programExercises = nw.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
            let pe = nw.programExercises.at(exercise.index); let idx = 0;
            pe.intervals = pe.intervals.sort((a, b) => a.index > b.index ? 1 : -1).filter(x => x.index !== intervalIndex).map(v => { v.index = idx; idx += 1; return v; });
            return nw;
        });
    };

    const deleteExercise = () => {
        setEditWorkout(prv => {
            let nw = { ...prv };
            if (exercise.superSetGroupId) { nw.programExercises = nw.programExercises.map(ex => ex.superSetGroupId === exercise.superSetGroupId && ex.index !== exercise.index ? { ...ex, superSetGroupId: null } : ex); }
            let idx = 0;
            nw.programExercises = nw.programExercises.sort((a, b) => a.index > b.index ? 1 : -1).filter(x => x.index !== exercise.index).map(v => { v.index = idx; idx += 1; return v; });
            return nw;
        });
    };

    const handleFieldChange = (field, value) => {
        setEditWorkout(prv => { let nw = { ...prv }; nw.programExercises.sort((a, b) => a.index > b.index ? 1 : -1).at(exercise.index)[field] = value; return nw; });
    };

    const handleIntervalFieldChange = (intervalIndex, field, value) => {
        setEditWorkout(prv => {
            let nw = { ...prv }; nw.programExercises = nw.programExercises.sort((a, b) => a.index > b.index ? 1 : -1);
            let interval = nw.programExercises.at(exercise.index).intervals.at(intervalIndex);
            interval[field] = value;
            if (field === 'typeSet' && value === 'MAX_REPS') interval.recommendedReps = null;
            if (field === 'typeSet' && value === 'RM_SET') interval.recommendedWeight = null;
            return nw;
        });
    };

    const toggleFinished = () => {
        setEditWorkout(prv => { let nw = { ...prv }; nw.programExercises.sort((a, b) => a.index > b.index ? 1 : -1).at(exercise.index).finished = !nw.programExercises.at(exercise.index).finished; return nw; });
    };

    if (loading) return null;

    const displayName = selectedValues.exerciseDef?.nameHeb || 'תרגיל חדש';
    // Named exerciseImage, not imageUrl, so it does not shadow the imageUrl() resize helper.
    const exerciseImage = selectedValues.exerciseDef?.imageUrl || selectedValues.exerciseDef?.coverUrl;
    const sets = exercise.intervals?.length || 0;
    const manipLabel = getManipulationLabel(exercise.manipulation);
    const muscleLabel = getTargetMuscleLabel(exercise.targetMuscle || selectedValues.targetMuscle);
    const isCardio = selectedValues.targetMuscle === 'CARDIO';
    const isCatalyst = exercise.catalyst || selectedValues.exerciseDef?.catalyst;
    const sortedIntervals = exercise.intervals?.sort((a, b) => a.index > b.index ? 1 : -1) || [];

    const prvBestWeight = prvExercise?.intervals?.reduce((max, i) => Math.max(max, i.weight || 0), 0) || null;
    const prvBestReps = prvExercise?.intervals?.reduce((max, i) => Math.max(max, i.reps || 0), 0) || null;

    return (
        <>
            <Paper elevation={1} sx={{
                backgroundColor: '#ffffff',
                borderRight: '4px solid',
                borderRightColor: exercise.finished ? '#22C55E' : isCardio ? '#E91E63' : isCatalyst ? '#FF6B00' : 'primary.main',
                borderRadius: 2, overflow: 'hidden',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { transform: 'translateY(-1px)' }
            }}>
                {/* Header row */}
                <Box onClick={() => !isCardio && setExpanded(!isExpanded)} sx={{
                    px: 1.5, py: 1, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                        <Avatar src={imageUrl(exerciseImage, 36)} variant="rounded" sx={{ width: 36, height: 36, borderRadius: '8px', backgroundColor: '#F1F5F9', flexShrink: 0 }}>
                            <FitnessCenterIcon sx={{ color: '#CBD5E1', fontSize: 18 }} />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {editMode ? (
                                <Box onClick={e => e.stopPropagation()}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {hasExerciseTarget && <Tooltip title="מטרה לתרגיל"><TargetIcon sx={{ fontSize: 16, color: '#EF4444' }} /></Tooltip>}
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                                            {displayName}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', mt: 0.25 }}>
                                        {isCardio
                                            ? `${exercise.durationTime || 0} דק׳${exercise.comment ? ` · ${exercise.comment}` : ''}`
                                            : `${sets} סטים · ${exercise.restTime}s מנוחה · ${manipLabel}${muscleLabel ? ` · ${muscleLabel}` : ''}`
                                        }
                                    </Typography>
                                    <TraineeSwapNotice
                                        replacedByTrainee={exercise.replacedByTrainee}
                                        originalExerciseNameHeb={exercise.originalExerciseNameHeb}
                                        originalExerciseName={exercise.originalExerciseName}
                                    />
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {hasExerciseTarget && <Tooltip title="מטרה לתרגיל"><TargetIcon sx={{ fontSize: 16, color: '#EF4444' }} /></Tooltip>}
                                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', mt: 0.25 }}>
                                        {isCardio
                                            ? `${exercise.durationTime || 0} דק׳${exercise.comment ? ` · ${exercise.comment}` : ''}`
                                            : `${sets} סטים · ${exercise.restTime}s מנוחה · ${manipLabel}${muscleLabel ? ` · ${muscleLabel}` : ''}`
                                        }
                                    </Typography>
                                    {prvBestWeight > 0 && <Typography sx={{ fontSize: '0.65rem', color: '#CBD5E1', mt: 0.15 }}>שבוע קודם: {prvBestWeight}kg × {prvBestReps}</Typography>}
                                    <TraineeSwapNotice
                                        replacedByTrainee={exercise.replacedByTrainee}
                                        originalExerciseNameHeb={exercise.originalExerciseNameHeb}
                                        originalExerciseName={exercise.originalExerciseName}
                                    />
                                </>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                        {/* History - always visible */}
                        <Tooltip title="היסטוריית תרגיל">
                            <IconButton size="small" onClick={() => setExerciseHistoryOpen(true)}
                                sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: '#0EA5E9' } }}>
                                <TimelineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>

                        {/* Edit mode actions */}
                        {editMode && (
                            <>
                                <Tooltip title="ערוך הגדרות">
                                    <IconButton size="small" onClick={() => setEditConfigDialogOpen(true)}
                                        sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: '#64748B' } }}>
                                        <EditIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="שנה תרגיל">
                                    <IconButton size="small" onClick={() => setChangeExerciseDialogOpen(true)}
                                        sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: '#F97316' } }}>
                                        <SwapHorizIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                                {!isPartOfSuperSet && !isCardio && onLinkAsSuperSet && (
                                    <Tooltip title="קשר כסופר סט">
                                        <IconButton size="small" onClick={() => setLinkSuperSetDialogOpen(true)}
                                            sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: SUPERSET_COLORS.accent } }}>
                                            <SyncAltIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip title="מחק תרגיל">
                                    <IconButton size="small" onClick={() => deleteExercise()}
                                        sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: 'error.main' } }}>
                                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}

                        {/* Done */}
                        <Tooltip title={exercise.finished ? "בוצע" : editMode ? "סמן כבוצע" : "לא בוצע"}>
                            <Box onClick={() => { if (editMode) toggleFinished(); }}
                                sx={{ cursor: editMode ? 'pointer' : 'default', display: 'flex', alignItems: 'center', px: 0.25 }}>
                                <CheckIcon sx={{ fontSize: 18, color: exercise.finished ? '#22C55E' : '#CBD5E1', transition: 'color 0.2s' }} />
                            </Box>
                        </Tooltip>

                        {/* Expand */}
                        {!isCardio && <IconButton size="small" onClick={() => setExpanded(!isExpanded)}
                            sx={{ color: 'primary.main', opacity: 0.6, p: 0.5, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                            <ExpandMoreOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>}
                    </Box>
                </Box>

                {/* (Edit config is now in a dialog, triggered by the edit icon in the header) */}

                {/* Catalyst intervals - collapsible (duration + weight grid) */}
                {isCatalyst && sortedIntervals.length > 0 && (
                    <Collapse in={isExpanded} timeout={200}>
                    <Box sx={{ px: 1.5, pb: 1, pt: 0.5, borderTop: '1px solid #F1F5F9' }}>
                        {/* Header row */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '30px 75px 80px 1fr 1fr 1fr' + (editMode ? ' 28px' : ''), gap: '4px', mb: 0.5, pb: 0.5, borderBottom: '1px solid #F1F5F9' }}>
                            <Typography sx={colHeaderSx}>סט</Typography>
                            <Typography sx={colHeaderSx}>סוג סט</Typography>
                            <Typography sx={{ ...colHeaderSx, color: '#FF6B00' }}>טווח (שניות)</Typography>
                            <Typography sx={colHeaderSx}>משך מומלץ</Typography>
                            <Typography sx={colHeaderSx}>משקל מומלץ</Typography>
                            <Typography sx={colHeaderSx}>משקל שבוצע</Typography>
                            {editMode && <Typography sx={colHeaderSx}></Typography>}
                        </Box>

                        {/* Interval rows */}
                        {sortedIntervals.map((interval, idx) => (
                            <Box key={interval.id || idx} sx={{
                                display: 'grid', gridTemplateColumns: '30px 75px 80px 1fr 1fr 1fr' + (editMode ? ' 28px' : ''), gap: '4px',
                                py: editMode ? 0.75 : 0.5, alignItems: 'start',
                                borderBottom: idx < sortedIntervals.length - 1 ? '1px solid #F1F5F9' : 'none',
                                backgroundColor: editMode ? '#FAFBFC' : 'transparent',
                                borderRadius: editMode ? '6px' : 0, px: editMode ? 0.5 : 0,
                            }}>
                                {/* Set # */}
                                <Box sx={{ width: 26, height: 26, borderRadius: '50%', backgroundColor: '#FFF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#FF6B00', mx: 'auto' }}>
                                    {interval.index + 1}
                                </Box>
                                {/* Set type */}
                                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', textAlign: 'center' }}>{typeSetLabels[interval.typeSet] || interval.typeSet}</Typography>
                                {/* Duration range */}
                                {editMode ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, justifyContent: 'center', direction: 'ltr' }}>
                                        <input type="number" {...nonNegativeInputProps} value={interval.minDuration ?? ''} placeholder="-" onChange={e => handleIntervalRangeChange(interval.index, 'minDuration', parseIntOrNull(e.target.value))}
                                            style={editInputStyle('#FFF8F0', '#FF6B00', '#FFD9B3', 28)} />
                                        <span style={{ fontSize: '0.7rem', color: '#FF6B00', fontWeight: 600 }}>-</span>
                                        <input type="number" {...nonNegativeInputProps} value={interval.maxDuration ?? ''} placeholder="-" onChange={e => handleIntervalRangeChange(interval.index, 'maxDuration', parseIntOrNull(e.target.value))}
                                            style={editInputStyle('#FFF8F0', '#FF6B00', '#FFD9B3', 28)} />
                                    </Box>
                                ) : (
                                    <Typography sx={{ fontSize: '0.8rem', color: '#FF6B00', textAlign: 'center', fontWeight: 500 }}>
                                        {interval.minDuration && interval.maxDuration ? `${interval.minDuration}-${interval.maxDuration}` : '-'}
                                    </Typography>
                                )}
                                {/* Recommended duration */}
                                <Box sx={{ textAlign: 'center' }}>
                                    {editMode ? (
                                        <input type="number" {...nonNegativeInputProps} value={interval.recommendedDuration ?? ''} onChange={e => handleIntervalFieldChange(interval.index, 'recommendedDuration', nonNegative(e.target.value))}
                                            style={editInputStyle('white', '#475569', '#E2E8F0', 56)} />
                                    ) : (
                                        <Typography sx={{ fontSize: '0.8rem', color: '#475569', textAlign: 'center' }}>
                                            {interval.recommendedDuration != null ? `${interval.recommendedDuration}s` : '-'}
                                        </Typography>
                                    )}
                                </Box>
                                {/* Recommended weight */}
                                <Box sx={{ textAlign: 'center' }}>
                                    {editMode ? (
                                        <input type="number" {...nonNegativeInputProps} value={interval.recommendedWeight ?? ''} onChange={e => handleIntervalFieldChange(interval.index, 'recommendedWeight', nonNegative(e.target.value))}
                                            style={editInputStyle('white', '#475569', '#E2E8F0', 64)} />
                                    ) : (
                                        <Typography sx={{ fontSize: '0.8rem', color: '#475569', textAlign: 'center' }}>
                                            {interval.recommendedWeight != null ? parseFloat(interval.recommendedWeight)?.toFixed(1) : '-'}
                                        </Typography>
                                    )}
                                </Box>
                                {/* Performed weight */}
                                <Box sx={{ textAlign: 'center' }}>
                                    {editMode ? (
                                        <input type="number" {...nonNegativeInputProps} value={interval.weight ?? ''} onChange={e => handleIntervalFieldChange(interval.index, 'weight', nonNegative(e.target.value))}
                                            style={{ ...editInputStyle('white', '#1E293B', '#E2E8F0', 64), fontWeight: 600 }} />
                                    ) : (
                                        <Typography sx={{ fontSize: '0.8rem', color: interval.weight ? '#1E293B' : '#CBD5E1', textAlign: 'center', fontWeight: interval.weight ? 700 : 400 }}>
                                            {interval.weight ?? '-'}
                                        </Typography>
                                    )}
                                </Box>
                                {/* Delete interval */}
                                {editMode && (
                                    <Box sx={{ textAlign: 'center' }}>
                                        <IconButton onClick={() => deleteInterval(interval.index)} size="small" sx={{ color: '#94A3B8', p: 0.25, '&:hover': { color: 'error.main' } }}>
                                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        ))}

                        {/* Add set button */}
                        {editMode && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.5 }}>
                                <IconButton onClick={addInterval} size="small" sx={{ color: '#FF6B00', p: 0.25 }}>
                                    <AddIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                    </Collapse>
                )}

                {/* Strength intervals - collapsible */}
                {!isCardio && !isCatalyst && sortedIntervals.length > 0 && (
                    <Collapse in={isExpanded} timeout={200}>
                    <Box sx={{ px: 1.5, pb: 1, pt: 0.5, borderTop: '1px solid #F1F5F9' }}>
                        {/* Header row */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, gap: '4px', mb: 0.5, pb: 0.5, borderBottom: '1px solid #F1F5F9' }}>
                            <Typography sx={colHeaderSx}>סט</Typography>
                            <Typography sx={colHeaderSx}>סוג סט</Typography>
                            <Box />
                            <Typography sx={{ ...colHeaderSx, color: '#7C3AED' }}>טווח</Typography>
                            <Typography sx={colHeaderSx}>חזרות מומלצות</Typography>
                            <Typography sx={colHeaderSx}>חזרות שבוצעו</Typography>
                            <Typography sx={colHeaderSx}>משקל מומלץ</Typography>
                            <Typography sx={colHeaderSx}>משקל שבוצע</Typography>
                            <Typography sx={colHeaderSx}>RIR</Typography>
                            {editMode && <Typography sx={colHeaderSx}></Typography>}
                        </Box>

                        {/* Interval rows */}
                        {sortedIntervals.map((interval, idx) => {
                            const prvInterval = prvExercise?.intervals?.sort((a, b) => a.index > b.index ? 1 : -1).at(idx);
                            const range = exerciseRange.intervals?.find(i => i.index === interval.index);
                            const hasRange = range?.minReps != null || range?.maxReps != null;

                            return (
                                <Box key={interval.id || idx} sx={{
                                    display: 'grid', gridTemplateColumns: gridCols, gap: '4px',
                                    py: editMode ? 0.75 : 0.5, alignItems: 'start',
                                    borderBottom: idx < sortedIntervals.length - 1 ? '1px solid #F1F5F9' : 'none',
                                    backgroundColor: editMode ? '#FAFBFC' : 'transparent',
                                    borderRadius: editMode ? '6px' : 0,
                                    px: editMode ? 0.5 : 0,
                                }}>
                                    {/* Set # */}
                                    <Box sx={{ width: 26, height: 26, borderRadius: '50%', backgroundColor: editMode ? '#E2E8F0' : '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#475569', mx: 'auto' }}>
                                        {interval.index + 1}
                                    </Box>

                                    {/* Set type */}
                                    <Box sx={{ textAlign: 'center' }}>
                                        {editMode ? (
                                            <RTL>
                                                <FormControl size="small" fullWidth>
                                                    <Select value={interval.typeSet || ""} onChange={(e) => handleIntervalFieldChange(interval.index, 'typeSet', e.target.value)} displayEmpty
                                                        renderValue={(val) => typeSetLabels[val] || val}
                                                        sx={{
                                                            height: 28, fontSize: '0.75rem',
                                                            backgroundColor: 'white', borderRadius: '6px',
                                                            '& .MuiSelect-select': { py: 0.25, px: 1 },
                                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#94A3B8' },
                                                        }}>
                                                        {typeSets.filter(ts => ts !== 'SUPER_SET').map((ts, i) => <MenuItem key={i} value={ts} sx={{ fontSize: '0.75rem' }}>{typeSetLabels[ts] || ts}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            </RTL>
                                        ) : (
                                            <Typography sx={{ fontSize: '0.75rem', color: '#64748B', textAlign: 'center' }}>{typeSetLabels[interval.typeSet] || interval.typeSet}</Typography>
                                        )}
                                    </Box>

                                    {/* Spacer */}
                                    <Box />

                                    {/* Range */}
                                    {editMode ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, justifyContent: 'center', direction: 'ltr' }}>
                                            <input type="number" {...nonNegativeInputProps} value={range?.minReps ?? ''} placeholder="-" onChange={e => handleIntervalRangeChange(interval.index, 'minReps', parseIntOrNull(e.target.value))}
                                                style={editInputStyle('#F5F3FF', '#7C3AED', '#DDD6FE', 36)} />
                                            <span style={{ fontSize: '0.7rem', color: '#8B5CF6', fontWeight: 600 }}>-</span>
                                            <input type="number" {...nonNegativeInputProps} value={range?.maxReps ?? ''} placeholder="-" onChange={e => handleIntervalRangeChange(interval.index, 'maxReps', parseIntOrNull(e.target.value))}
                                                style={editInputStyle('#F5F3FF', '#7C3AED', '#DDD6FE', 36)} />
                                        </Box>
                                    ) : (
                                        <Typography sx={{ fontSize: '0.8rem', color: '#8B5CF6', textAlign: 'center', fontWeight: 500 }}>
                                            {hasRange ? `${range.minReps || '?'}-${range.maxReps || '?'}` : '-'}
                                        </Typography>
                                    )}

                                    {/* Recommended reps */}
                                    <Box sx={{ textAlign: 'center' }}>
                                        {editMode ? (
                                            <input type="number" {...nonNegativeInputProps} value={interval.recommendedReps ?? ''} onChange={e => handleIntervalFieldChange(interval.index, 'recommendedReps', nonNegative(e.target.value))}
                                                style={editInputStyle('white', '#475569', '#E2E8F0', 56)} />
                                        ) : (
                                            <Typography sx={{ fontSize: '0.8rem', color: '#475569', textAlign: 'center' }}>
                                                {interval.recommendedReps ?? 'מקסימום חזרות'}
                                            </Typography>
                                        )}
                                        {prvInterval?.recommendedReps != null && (
                                            <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', lineHeight: 1.2 }}>שבוע קודם: {prvInterval.recommendedReps}</Typography>
                                        )}
                                    </Box>

                                    {/* Completed reps */}
                                    <Box sx={{ textAlign: 'center' }}>
                                        {editMode ? (
                                            <input type="number" {...nonNegativeInputProps} value={interval.reps ?? ''} onChange={e => handleIntervalFieldChange(interval.index, 'reps', nonNegative(e.target.value))}
                                                style={{ ...editInputStyle('white', '#1E293B', '#E2E8F0', 56), fontWeight: 600 }} />
                                        ) : (
                                            <Typography sx={{ fontSize: '0.8rem', color: interval.reps ? '#1E293B' : '#CBD5E1', textAlign: 'center', fontWeight: interval.reps ? 600 : 400 }}>
                                                {interval.reps ?? '-'}
                                            </Typography>
                                        )}
                                        {prvInterval?.reps != null && (
                                            <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', lineHeight: 1.2 }}>שבוע קודם: {prvInterval.reps}</Typography>
                                        )}
                                    </Box>

                                    {/* Recommended weight */}
                                    <Box sx={{ textAlign: 'center' }}>
                                        {editMode ? (
                                            <input type="number" {...nonNegativeInputProps} value={interval.recommendedWeight ?? ''} onChange={e => handleIntervalFieldChange(interval.index, 'recommendedWeight', nonNegative(e.target.value))}
                                                style={editInputStyle('white', '#475569', '#E2E8F0', 64)} />
                                        ) : (
                                            <Typography sx={{ fontSize: '0.8rem', color: '#475569', textAlign: 'center' }}>
                                                {interval.recommendedWeight != null ? parseFloat(interval.recommendedWeight)?.toFixed(1) : 'מקסימום משקל'}
                                            </Typography>
                                        )}
                                        {prvInterval?.recommendedWeight != null && (
                                            <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', lineHeight: 1.2 }}>שבוע קודם: {prvInterval.recommendedWeight}</Typography>
                                        )}
                                    </Box>

                                    {/* Completed weight */}
                                    <Box sx={{ textAlign: 'center' }}>
                                        {editMode ? (
                                            <input type="number" {...nonNegativeInputProps} value={interval.weight ?? ''} onChange={e => handleIntervalFieldChange(interval.index, 'weight', nonNegative(e.target.value))}
                                                style={{ ...editInputStyle('white', '#1E293B', '#E2E8F0', 64), fontWeight: 600 }} />
                                        ) : (
                                            <Typography sx={{ fontSize: '0.8rem', color: interval.weight ? '#1E293B' : '#CBD5E1', textAlign: 'center', fontWeight: interval.weight ? 700 : 400 }}>
                                                {interval.weight ?? '-'}
                                            </Typography>
                                        )}
                                        {prvInterval?.weight != null && (
                                            <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', lineHeight: 1.2 }}>שבוע קודם: {prvInterval.weight}</Typography>
                                        )}
                                    </Box>

                                    {/* RIR */}
                                    <Box sx={{ textAlign: 'center' }}>
                                        {editMode ? (
                                            <input type="number" {...nonNegativeInputProps} value={interval.rir ?? ''} onChange={e => handleIntervalFieldChange(interval.index, 'rir', nonNegative(e.target.value))}
                                                style={editInputStyle('white', '#475569', '#E2E8F0', 44)} />
                                        ) : (
                                            <Typography sx={{ fontSize: '0.8rem', color: '#475569', fontWeight: interval.rir != null ? 700 : 400, textAlign: 'center' }}>
                                                {interval.rir ?? '-'}
                                            </Typography>
                                        )}
                                        {prvInterval?.rir != null && (
                                            <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', lineHeight: 1.2 }}>שבוע קודם: {prvInterval.rir}</Typography>
                                        )}
                                    </Box>

                                    {/* Delete interval */}
                                    {editMode && (
                                        <Box sx={{ textAlign: 'center' }}>
                                            <IconButton onClick={() => deleteInterval(interval.index)} size="small" sx={{ color: '#94A3B8', p: 0.25, '&:hover': { color: 'error.main' } }}>
                                                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}

                        {/* Add set button */}
                        {editMode && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.5 }}>
                                <IconButton onClick={addInterval} size="small" sx={{ color: 'primary.main', p: 0.25 }}>
                                    <AddIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                    </Collapse>
                )}

            </Paper>

            <ExerciseHistoryDialog
                isOpen={exerciseHistoryOpen}
                close={() => { setExerciseHistoryOpen(false); refreshExerciseTarget(); }}
                exerciseName={selectedValues.exerciseDef?.nameHeb}
                exerciseId={exercise.exerciseId}
                exerciseProgramId={exercise.id}
                traineeId={traineeId}
                isCompleted={exercise.finished}
            />

            <AddExercisesDialog
                isOpen={changeExerciseDialogOpen}
                close={() => setChangeExerciseDialogOpen(false)}
                onAdd={handleChangeExerciseFromDialog}
                title="החלף תרגיל"
                initialTargetMuscle={targetMuscles.find(t => t.value === selectedValues.targetMuscle) || null}
                currentExerciseId={exercise.exerciseId}
            />

            <Dialog
                open={linkSuperSetDialogOpen}
                onClose={() => setLinkSuperSetDialogOpen(false)}
                PaperProps={{ sx: { borderRadius: '14px', minWidth: { xs: '90%', sm: 420 }, maxWidth: 480 } }}
            >
                <DialogTitle sx={{
                    fontWeight: 600, fontSize: '1.1rem', color: '#1E293B',
                    textAlign: 'center', pb: 1, borderBottom: '1px solid #E2E8F0',
                }}>
                    בחר תרגיל לסופר סט
                </DialogTitle>
                <DialogContent sx={{ pt: 2, direction: 'rtl' }}>
                    <RTL>
                        {(() => {
                            const eligible = (allExercises || []).filter(ex =>
                                Math.abs(ex.index - exercise.index) === 1 &&
                                !ex.superSetGroupId &&
                                ex.targetMuscle !== 'CARDIO'
                            );
                            if (eligible.length === 0) {
                                return (
                                    <Typography sx={{ textAlign: 'center', color: '#94A3B8', py: 3, fontSize: '0.9rem' }}>
                                        אין תרגילים סמוכים פנויים לסופר סט
                                    </Typography>
                                );
                            }
                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                                    {eligible.map(ex => {
                                        const muscleLabel = getTargetMuscleLabel(ex.targetMuscle);
                                        return (
                                            <Box key={ex.uuid || ex.id || ex.index}
                                                onClick={() => {
                                                    onLinkAsSuperSet(ex);
                                                    setLinkSuperSetDialogOpen(false);
                                                }}
                                                sx={{
                                                    display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25,
                                                    borderRadius: '10px', border: '1px solid #E2E8F0', cursor: 'pointer',
                                                    transition: 'all 0.15s ease',
                                                    '&:hover': {
                                                        borderColor: SUPERSET_COLORS.accent,
                                                        backgroundColor: SUPERSET_COLORS.background,
                                                    },
                                                }}>
                                                <Avatar variant="rounded"
                                                    sx={{ width: 36, height: 36, borderRadius: '8px', backgroundColor: '#F1F5F9' }}>
                                                    <FitnessCenterIcon sx={{ color: '#CBD5E1', fontSize: 18 }} />
                                                </Avatar>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {ex.exerciseNameHeb || ex.exerciseName || `תרגיל ${ex.index + 1}`}
                                                    </Typography>
                                                    {muscleLabel && (
                                                        <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', mt: 0.25 }}>
                                                            {muscleLabel}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            );
                        })()}
                    </RTL>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, pt: 1, borderTop: '1px solid #E2E8F0', justifyContent: 'center' }}>
                    <Button
                        onClick={() => setLinkSuperSetDialogOpen(false)}
                        variant="text"
                        sx={{ color: '#64748B', textTransform: 'none', fontWeight: 600 }}
                    >
                        בטל
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Config Dialog */}
            <Dialog
                open={editConfigDialogOpen}
                onClose={() => setEditConfigDialogOpen(false)}
                PaperProps={{ sx: { borderRadius: '14px', minWidth: { xs: '90%', sm: 400 }, maxWidth: 440 } }}
            >
                <DialogTitle sx={{
                    fontWeight: 600, fontSize: '1.1rem', color: '#1E293B',
                    textAlign: 'center', pb: 1, borderBottom: '1px solid #E2E8F0',
                }}>
                    הגדרות תרגיל
                </DialogTitle>
                <DialogContent sx={{ pt: 3, direction: 'rtl' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                        {/* Duration - cardio only */}
                        {isCardio && (
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 500, color: '#64748B', mb: 0.5, display: 'block' }}>
                                    משך (דקות)
                                </Typography>
                                <TextField
                                    value={exercise.durationTime || 0}
                                    onChange={e => handleFieldChange('durationTime', parseInt(nonNegative(e.target.value)) || 0)}
                                    type="number"
                                    size="small"
                                    fullWidth
                                    inputProps={{ ...nonNegativeInputProps, min: 1, max: 999 }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                />
                            </Box>
                        )}

                        {/* Manipulation - strength only */}
                        {!isCardio && (
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 500, color: '#64748B', mb: 0.5, display: 'block' }}>
                                    מניפולציה
                                </Typography>
                                <RTL>
                                    <FormControl size="small" fullWidth>
                                        <Select
                                            value={exercise.manipulation || ""}
                                            onChange={(e) => handleFieldChange('manipulation', e.target.value)}
                                            displayEmpty
                                            sx={{ borderRadius: '8px' }}
                                        >
                                            {ManipulationTypes.map((t, i) => <MenuItem key={i} value={t.value}>{t.label}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </RTL>
                            </Box>
                        )}

                        {/* Tempo - strength only */}
                        {!isCardio && (
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 500, color: '#64748B', mb: 0.5, display: 'block' }}>
                                    קצב תנועה
                                </Typography>
                                <RTL>
                                    <FormControl size="small" fullWidth>
                                        <Select
                                            value={exercise.tempo || ""}
                                            onChange={(e) => handleFieldChange('tempo', e.target.value)}
                                            displayEmpty
                                            sx={{ borderRadius: '8px' }}
                                        >
                                            {TempoTypes.map((r, i) => <MenuItem key={i} value={r.value}>{r.name}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </RTL>
                            </Box>
                        )}

                        {/* Rest Time - strength only */}
                        {!isCardio && (
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 500, color: '#64748B', mb: 0.5, display: 'block' }}>
                                    זמן מנוחה (שניות)
                                </Typography>
                                <TextField
                                    value={exercise.restTime}
                                    onChange={e => handleFieldChange('restTime', nonNegative(e.target.value))}
                                    type="number"
                                    size="small"
                                    fullWidth
                                    inputProps={nonNegativeInputProps}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                />
                            </Box>
                        )}

                        {/* Notes */}
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: '#64748B', mb: 0.5, display: 'block' }}>
                                הערות
                            </Typography>
                            <TextField
                                value={exercise.comment || ''}
                                onChange={e => handleFieldChange('comment', e.target.value)}
                                multiline
                                rows={3}
                                size="small"
                                fullWidth
                                placeholder="הערות על התרגיל..."
                                sx={{
                                    '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                                    '& .MuiInputBase-input': { textAlign: 'right', direction: 'rtl' }
                                }}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, pt: 1, borderTop: '1px solid #E2E8F0', justifyContent: 'center' }}>
                    <Button
                        onClick={() => setEditConfigDialogOpen(false)}
                        variant="contained"
                        sx={{
                            backgroundColor: '#57837E', borderRadius: '8px',
                            textTransform: 'none', fontWeight: 600, px: 4,
                            '&:hover': { backgroundColor: '#456b67' }
                        }}
                    >
                        שמור
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const gridCols = '30px 75px 12px 95px 1fr 1fr 1fr 1fr 80px 28px';

const typeSetLabels = {
    'REGULAR_SET': 'רגיל',
    'MAX_REPS': 'מקס חזרות',
    'SUPER_SET': 'סופר סט',
    'DROP_SET': 'דרופ',
    'CLUSTER_SET': 'קלאסטר',
    'REST_PAUSE': 'מנוחה',
    'MYO_REPS': 'מיו',
    'RM_SET': 'RM',
};
const colHeaderSx = { fontSize: '0.7rem', fontWeight: 700, color: '#64748B', textAlign: 'center', lineHeight: 1.2 };

/**
 * Range fields store a number rather than the raw string. A negative (only reachable by paste —
 * `-` is blocked on keydown) becomes null, so nothing below zero is persisted.
 */
const parseIntOrNull = (raw) => {
    const value = nonNegative(raw);
    return value ? parseInt(value) : null;
};

const editInputStyle = (bg, color, borderColor, width) => ({
    width,
    textAlign: 'center',
    border: `1px solid ${borderColor}`,
    borderRadius: '6px',
    padding: '4px 2px',
    fontSize: '0.8rem',
    color,
    outline: 'none',
    backgroundColor: bg,
    transition: 'border-color 0.2s',
});

export default ProgramExerciseCard;
